import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, MessageCircle, Send, Loader2, Inbox, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Input, Label } from "../components/ui/Input";
import Button from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [view, setView] = useState("form");
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");

  const [formData, setFormData] = useState({
    subject: "",
    body: "",
    category: "general",
    ticket_type: "message",
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  const STATUS_LABELS = {
    new: { label: t("contact.statusNew"), color: "bg-arcane/20 text-arcane border-arcane" },
    open: { label: t("contact.statusOpen"), color: "bg-gold/20 text-gold border-gold" },
    awaiting_user: {
      label: t("contact.statusAwaitingUser"),
      color: "bg-gold/20 text-gold border-gold",
    },
    awaiting_admin: {
      label: t("contact.statusAwaitingAdmin"),
      color: "bg-arcane/20 text-arcane border-arcane",
    },
    closed: {
      label: t("contact.statusClosed"),
      color: "bg-muted/20 text-muted-foreground border-border",
    },
  };

  const CATEGORY_LABELS = {
    general: t("contact.catGeneral"),
    billing: t("contact.catBilling"),
    technical: t("contact.catTechnical"),
    bug: t("contact.catBug"),
    feature_request: t("contact.catFeature"),
    premium: t("contact.catPremium"),
  };

  const dateLocale = i18n.language && i18n.language.startsWith("it") ? "it-IT" : "en-US";

  useEffect(() => {
    if (user && view === "list") fetchTickets();
  }, [user, view]);

  const fetchTickets = async () => {
    try {
      setLoadingList(true);
      const r = await fetch(`${API}/api/support/tickets`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setTickets(d.tickets || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingList(false);
    }
  };

  const openTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setView("detail");
    setLoadingDetail(true);
    try {
      const r = await fetch(`${API}/api/support/tickets/${ticket.ticket_id}`, {
        credentials: "include",
      });
      if (r.ok) {
        const d = await r.json();
        setSelectedTicket(d.ticket);
        setMessages(d.messages || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      alert(t("contact.loginRequired"));
      return;
    }
    try {
      setSubmitting(true);
      const r = await fetch(`${API}/api/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      if (!r.ok) {
        const err = await r.json();
        alert(`${t("contact.errorPrefix")} ${err.detail || t("contact.errorSend")}`);
        return;
      }
      setFormData({ subject: "", body: "", category: "general", ticket_type: "message" });
      alert(t("contact.sentSuccess"));
      setView("list");
    } catch (e) {
      alert(t("contact.networkError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    try {
      setReplying(true);
      const r = await fetch(
        `${API}/api/support/tickets/${selectedTicket.ticket_id}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ body: replyText.trim() }),
        },
      );
      if (r.ok) {
        setReplyText("");
        await openTicket(selectedTicket);
      } else {
        const err = await r.json();
        alert(`${t("contact.errorPrefix")} ${err.detail || t("contact.errorSend")}`);
      }
    } catch (e) {
      alert(t("contact.networkError"));
    } finally {
      setReplying(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <h1 className="font-display text-5xl md:text-6xl text-gold mb-4" data-testid="contact-title">
            {t("contact.title")}
          </h1>
          <p className="font-serif-fantasy italic text-lg text-parchment/90 max-w-2xl mx-auto">
            {t("contact.quote")}
          </p>
        </div>

        {user && (
          <div className="flex gap-2 mb-8 border-b border-border">
            <button
              onClick={() => setView("form")}
              data-testid="contact-tab-new"
              className={`flex items-center gap-2 px-4 py-3 font-heading text-sm transition-colors border-b-2 ${
                view === "form"
                  ? "border-gold text-gold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <MessageCircle className="w-4 h-4" />
              {t("contact.tabNew")}
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="contact-tab-list"
              className={`flex items-center gap-2 px-4 py-3 font-heading text-sm transition-colors border-b-2 ${
                view === "list" || view === "detail"
                  ? "border-gold text-gold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Inbox className="w-4 h-4" />
              {t("contact.tabList")}
            </button>
          </div>
        )}

        {view === "form" && (
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <MessageCircle className="w-5 h-5 mr-2 text-gold" />
                    {formData.ticket_type === "ticket"
                      ? t("contact.openTicket")
                      : t("contact.sendMessage")}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!user && (
                    <div className="mb-4 p-3 bg-gold/10 border border-gold rounded-lg text-sm">
                      <Link to="/login" className="text-gold underline font-heading">
                        {t("contact.loginPromptCta")}
                      </Link>
                      {t("contact.loginPromptSuffix")}
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label>{t("contact.type")}</Label>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {[
                          { value: "message", label: t("contact.typeMessage") },
                          { value: "ticket", label: t("contact.typeTicket") },
                        ].map((tt) => (
                          <button
                            key={tt.value}
                            type="button"
                            onClick={() =>
                              setFormData({ ...formData, ticket_type: tt.value })
                            }
                            data-testid={`contact-type-${tt.value}`}
                            className={`px-4 py-2 rounded-lg text-sm font-heading border transition-colors ${
                              formData.ticket_type === tt.value
                                ? "bg-gold text-background border-gold"
                                : "bg-background text-foreground border-border hover:border-gold"
                            }`}
                          >
                            {tt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="category">{t("contact.category")}</Label>
                      <select
                        id="category"
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className="mt-2 w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                        data-testid="contact-category"
                      >
                        {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="subject">{t("contact.subject")}</Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        placeholder={t("contact.subjectPlaceholder")}
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div>
                      <Label htmlFor="body">{t("contact.message")}</Label>
                      <textarea
                        id="body"
                        name="body"
                        rows="6"
                        placeholder={t("contact.messagePlaceholder")}
                        value={formData.body}
                        onChange={handleChange}
                        required
                        className="mt-2 flex w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                        data-testid="contact-body"
                      />
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                      disabled={submitting || !user}
                      data-testid="contact-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t("contact.sending")}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {t("contact.send")}
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-foreground mb-2">
                        {t("contact.email")}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        {t("contact.emailDesc")}
                      </p>
                      <a
                        href="mailto:support@mytharion.app"
                        className="text-gold hover:text-gold/80 text-sm"
                      >
                        support@mytharion.app
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-ruby/20 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-ruby" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-foreground mb-2">
                        {t("contact.office")}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {t("contact.officeAddr")}
                        <br />
                        {t("contact.officeTag")}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="rune-border bg-card/50 backdrop-blur rounded-xl p-6">
                <h3 className="font-heading text-lg text-gold mb-3">
                  {t("contact.responseTitle")}
                </h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-gold rounded-full mr-3"></span>
                    {t("contact.responseGeneral")}
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                    {t("contact.responsePremium")}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {view === "list" && user && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Inbox className="w-5 h-5 mr-2 text-gold" />
                {t("contact.myTickets")} ({tickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingList ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-gold animate-spin" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="py-12 text-center">
                  <MessageCircle className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground mb-4">{t("contact.noTickets")}</p>
                  <Button variant="primary" onClick={() => setView("form")}>
                    {t("contact.createNew")}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((tk) => {
                    const s = STATUS_LABELS[tk.status] || STATUS_LABELS.new;
                    return (
                      <button
                        key={tk.ticket_id}
                        onClick={() => openTicket(tk)}
                        data-testid={`ticket-item-${tk.ticket_id}`}
                        className="w-full text-left p-4 border border-border rounded-lg hover:border-gold transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="font-heading text-foreground truncate">
                                {tk.subject}
                              </p>
                              <span
                                className={`text-xs px-2 py-0.5 rounded font-heading border ${s.color}`}
                              >
                                {s.label}
                              </span>
                              {tk.unread_by_user > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded bg-ruby text-white font-heading">
                                  {t("contact.newBadge", { count: tk.unread_by_user })}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {CATEGORY_LABELS[tk.category] || tk.category} ·{" "}
                              {new Date(tk.last_message_at).toLocaleString(dateLocale)}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground mt-1" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {view === "detail" && selectedTicket && (
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <button
                    onClick={() => {
                      setView("list");
                      setSelectedTicket(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-gold mb-2"
                  >
                    {t("contact.backToList")}
                  </button>
                  <CardTitle data-testid="ticket-detail-subject">
                    {selectedTicket.subject}
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {CATEGORY_LABELS[selectedTicket.category] || selectedTicket.category}
                  </p>
                </div>
                {(() => {
                  const s = STATUS_LABELS[selectedTicket.status] || STATUS_LABELS.new;
                  return (
                    <span
                      className={`text-xs px-3 py-1 rounded font-heading border ${s.color}`}
                    >
                      {s.label}
                    </span>
                  );
                })()}
              </div>
            </CardHeader>
            <CardContent>
              {loadingDetail ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-8 h-8 mx-auto text-gold animate-spin" />
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
                    {messages.map((m) => {
                      const isMine =
                        m.sender_role === "user" && m.sender_id === user?.user_id;
                      return (
                        <div
                          key={m.message_id}
                          className={`flex ${isMine ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-lg border ${
                              isMine
                                ? "bg-gold/10 border-gold/50"
                                : m.sender_role === "admin"
                                ? "bg-arcane/10 border-arcane/50"
                                : "bg-card border-border"
                            }`}
                            data-testid={`ticket-msg-${m.message_id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-xs font-heading ${
                                  m.sender_role === "admin" ? "text-arcane" : "text-gold"
                                }`}
                              >
                                {m.sender_role === "admin"
                                  ? t("contact.msgSupport")
                                  : m.sender_name || t("contact.msgYou")}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(m.created_at).toLocaleString(dateLocale)}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedTicket.status !== "closed" ? (
                    <form onSubmit={handleReply} className="space-y-3">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows="4"
                        placeholder={t("contact.writeReply")}
                        className="w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                        data-testid="ticket-reply-body"
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={replying || !replyText.trim()}
                        data-testid="ticket-reply-submit"
                      >
                        {replying ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t("contact.sendingReply")}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            {t("contact.sendReply")}
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <div className="p-4 bg-muted/20 border border-border rounded-lg text-center">
                      <p className="text-sm text-muted-foreground mb-2">
                        {t("contact.closedTitle")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("contact.closedInfo")}
                      </p>
                      <form onSubmit={handleReply} className="mt-3 space-y-3">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          rows="3"
                          placeholder={t("contact.reopenPlaceholder")}
                          className="w-full rounded-md bg-input border border-input px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-ring focus:outline-none"
                          data-testid="ticket-reopen-body"
                        />
                        <Button
                          type="submit"
                          variant="secondary"
                          disabled={replying || !replyText.trim()}
                          data-testid="ticket-reopen-submit"
                        >
                          {replying ? t("contact.reopenSending") : t("contact.reopen")}
                        </Button>
                      </form>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContactPage;
